import type { ColumnDef } from '@tanstack/react-table'
import moment from 'moment'
import { decodeFilters, FilterValueType, type FilterableColumn, type FilterOperator } from 'app/filter.utils'
import { AnchorCell } from 'app/components/table/AnchorCell'
import MetricCell from 'app/components/table/MetricCell'
import { openLocationUsageDetail } from 'app/components/usage-tracking/LocationUsageDetailModal'
import { formatLatencyMs, formatUsdOrDash } from 'app/components/usage-tracking/usageCostFormat'
import type {
  UsageCostLocationRow,
  UsageCostSmsThreadRow,
  UsageCostVoiceCallRow,
} from 'app/components/usage-tracking/graphql'

export function getUsageLocationColumns({
  fromDate,
  toDate,
  baseUrl,
}: {
  fromDate: string
  toDate: string
  baseUrl: string
}): ColumnDef<UsageCostLocationRow, any>[] {
  return [
    {
      accessorKey: `locationName`,
      accessorFn: (row) => row.locationName ?? `—`,
      header: `Location Name`,
      id: `Location Name`,
      meta: {
        group: `General`,
        defaultVisible: true,
        pinned: true,
        // The href makes ctrl/cmd-click open the drill-down in a new tab; a plain click
        // is intercepted and opens the modal in place.
        ControlledComponent: (row) => (
          <AnchorCell
            href={`${baseUrl}${baseUrl.includes(`?`) ? `&` : `?`}locationDetail=${row?.locationId}`}
            tooltipMessage={row?.locationName ?? undefined}
            clickHandler={() => row && openLocationUsageDetail(row, fromDate, toDate)}
          >
            <span className="text-body-xs text-default">{row?.locationName ?? `—`}</span>
          </AnchorCell>
        ),
      },
      enableSorting: true,
      enableHiding: false,
    },
    {
      accessorKey: `workspaceName`,
      accessorFn: (row) => row.workspaceName ?? `—`,
      header: `Workspace`,
      id: `Workspace`,
      meta: {
        group: `General`,
        defaultVisible: true,
      },
      enableSorting: true,
    },
    {
      accessorKey: `voiceCalls`,
      accessorFn: (row) => row.voiceCalls,
      cell: (props) => <MetricCell value={props.row.original.voiceCalls} />,
      header: `Voice Calls`,
      id: `Voice Calls`,
      meta: {
        group: `Totals`,
        defaultVisible: true,
        infoTooltip: {
          title: `Voice Calls`,
          message: `Number of Voice Agent calls handled at this location within the date range.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.voiceCalls, 0)}
        />
      ),
    },
    {
      accessorKey: `smsConversations`,
      accessorFn: (row) => row.smsConversations,
      cell: (props) => <MetricCell value={props.row.original.smsConversations} />,
      header: `SMS Conversations`,
      id: `SMS Conversations`,
      meta: {
        group: `Totals`,
        defaultVisible: true,
        infoTooltip: {
          title: `SMS Conversations`,
          message: `Number of SMS Agent conversations handled at this location within the date range.`,
        },
      },
      footer: ({ table }) => (
        <MetricCell
          value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.smsConversations, 0)}
        />
      ),
    },
    {
      accessorKey: `voiceCostUsd`,
      accessorFn: (row) => row.voiceCostUsd,
      cell: (props) => <>{formatUsdOrDash(props.row.original.voiceCostUsd)}</>,
      header: `Voice Cost`,
      id: `Voice Cost`,
      meta: {
        group: `Financial`,
        defaultVisible: true,
        infoTooltip: {
          title: `Voice Cost`,
          message: `Voice Agent cost (ElevenLabs + Twilio telephony) at this location within the date range.`,
        },
      },
      footer: ({ table }) => (
        <>
          {formatUsdOrDash(
            table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.voiceCostUsd, 0),
          )}
        </>
      ),
    },
    {
      accessorKey: `smsCostUsd`,
      accessorFn: (row) => row.smsCostUsd,
      cell: (props) => <>{formatUsdOrDash(props.row.original.smsCostUsd)}</>,
      header: `SMS Cost`,
      id: `SMS Cost`,
      meta: {
        group: `Financial`,
        defaultVisible: true,
        infoTooltip: {
          title: `SMS Cost`,
          message: `SMS Agent cost (OpenAI tokens + Twilio segments) at this location within the date range.`,
        },
      },
      footer: ({ table }) => (
        <>
          {formatUsdOrDash(table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.smsCostUsd, 0))}
        </>
      ),
    },
    {
      accessorKey: `totalCostUsd`,
      accessorFn: (row) => row.totalCostUsd,
      cell: (props) => <>{formatUsdOrDash(props.row.original.totalCostUsd)}</>,
      header: `Total Cost`,
      id: `Total Cost`,
      meta: {
        group: `Financial`,
        defaultVisible: true,
      },
      footer: ({ table }) => (
        <>
          {formatUsdOrDash(
            table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.totalCostUsd, 0),
          )}
        </>
      ),
    },
    {
      accessorKey: `costPer100Calls`,
      accessorFn: (row) => (row.voiceCalls > 0 ? (row.voiceCostUsd / row.voiceCalls) * 100 : 0),
      cell: (props) => {
        const row = props.row.original
        const value = row.voiceCalls > 0 ? (row.voiceCostUsd / row.voiceCalls) * 100 : 0
        return <>{formatUsdOrDash(value)}</>
      },
      header: `Cost / 100 Calls`,
      id: `Cost / 100 Calls`,
      meta: {
        group: `Financial`,
        defaultVisible: true,
        infoTooltip: {
          title: `Cost / 100 Calls`,
          message: `Voice cost scaled to a readable per-100-call rate at this location within the date range.`,
        },
      },
    },
    {
      accessorKey: `costPer100Conversations`,
      accessorFn: (row) => (row.smsConversations > 0 ? (row.smsCostUsd / row.smsConversations) * 100 : 0),
      cell: (props) => {
        const row = props.row.original
        const value = row.smsConversations > 0 ? (row.smsCostUsd / row.smsConversations) * 100 : 0
        return <>{formatUsdOrDash(value)}</>
      },
      header: `Cost / 100 Conversations`,
      id: `Cost / 100 Conversations`,
      meta: {
        group: `Financial`,
        defaultVisible: true,
        infoTooltip: {
          title: `Cost / 100 Conversations`,
          message: `SMS cost scaled to a readable per-100-conversation rate at this location within the date range.`,
        },
      },
    },
    {
      accessorKey: `avgCostPerInteraction`,
      accessorFn: (row) => row.avgCostPerInteraction,
      cell: (props) => <>{formatUsdOrDash(props.row.original.avgCostPerInteraction ?? 0)}</>,
      header: `Avg Cost per Interaction`,
      id: `Avg Cost per Interaction`,
      meta: {
        group: `Financial`,
        defaultVisible: false,
        infoTooltip: {
          title: `Avg Cost per Interaction`,
          message: `Total cost divided by the number of interactions (voice calls + SMS conversations) at this location. Blends both products — superseded by the per-product cost columns.`,
        },
      },
    },
  ]
}

export type UsageDetailProduct = `voice` | `sms`

export const USAGE_DETAIL_PRODUCT_ATTRIBUTE = `product`

/**
 * Product picker for the location drill-down. It is a client-side switch between two
 * record sets rather than a search term, so it is deliberately the only filter offered
 * and its operator is locked.
 */
export function getUsageDetailFilterableColumns(): FilterableColumn[] {
  return [
    {
      attribute: USAGE_DETAIL_PRODUCT_ATTRIBUTE,
      displayName: `Product`,
      type: FilterValueType.boolean,
      uniqOperator: `exactly` as FilterOperator,
      defaultFilter: true,
      defaultOperator: `exactly` as FilterOperator,
      defaultValue: `voice`,
      options: [
        { id: `voice`, value: `voice`, name: `Voice Agent` },
        { id: `sms`, value: `sms`, name: `SMS Agent` },
      ],
    },
  ]
}

/** Read the product back out of the encoded filter string TableFilters emits. */
export function readUsageDetailProduct(encodedFilters: string): UsageDetailProduct {
  const product = decodeFilters(encodedFilters).find(
    (filter) => filter.attribute === USAGE_DETAIL_PRODUCT_ATTRIBUTE,
  )?.value
  const value = Array.isArray(product) ? product[0] : product
  // Clearing the filter leaves no product at all; fall back to the default rather than
  // rendering an empty table.
  return value === `sms` ? `sms` : `voice`
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return `—`
  const parsed = moment(value)
  return parsed.isValid() ? parsed.format(`MMM D, YYYY h:mm A`) : `—`
}

function formatDuration(seconds: number): string {
  if (seconds <= 0) return `—`
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, `0`)}`
}

// Footers sum the listed rows only. The drill-down lists the most expensive records,
// not every one, so this is a "how much of the row do these account for" subtotal.
function sumFooter<T>(select: (row: T) => number) {
  return ({ table }: { table: { getFilteredRowModel: () => { rows: { original: T }[] } } }) => (
    <>{formatUsdOrDash(table.getFilteredRowModel().rows.reduce((total, row) => total + select(row.original), 0))}</>
  )
}

export function getUsageVoiceCallColumns(): ColumnDef<UsageCostVoiceCallRow, any>[] {
  return [
    {
      accessorFn: (row) => row.callDate,
      cell: (props) => <>{formatDateTime(props.row.original.callDate)}</>,
      header: `Call`,
      id: `Call`,
      meta: { group: `General`, defaultVisible: true, pinned: true },
      enableHiding: false,
    },
    {
      accessorFn: (row) => row.callerPhone ?? `—`,
      header: `Caller`,
      id: `Caller`,
      meta: { group: `General`, defaultVisible: true },
    },
    {
      accessorFn: (row) => row.status ?? `—`,
      header: `Status`,
      id: `Status`,
      meta: { group: `General`, defaultVisible: true },
    },
    {
      accessorFn: (row) => row.transferred,
      cell: (props) => <>{props.row.original.transferred ? `Yes` : `No`}</>,
      header: `Transferred`,
      id: `Transferred`,
      meta: {
        group: `General`,
        defaultVisible: true,
        infoTooltip: {
          title: `Transferred`,
          message: `Whether the agent handed this call off to the office. A transfer still incurs the full AI leg cost.`,
        },
      },
    },
    {
      accessorFn: (row) => row.durationSeconds,
      cell: (props) => <>{formatDuration(props.row.original.durationSeconds)}</>,
      header: `Duration`,
      id: `Duration`,
      meta: { group: `General`, defaultVisible: true },
    },
    {
      accessorFn: (row) => row.telephonyCostUsd,
      cell: (props) => <>{formatUsdOrDash(props.row.original.telephonyCostUsd)}</>,
      header: `Telephony`,
      id: `Telephony`,
      meta: {
        group: `Cost Components`,
        defaultVisible: true,
        infoTooltip: {
          title: `Telephony`,
          message: `Twilio carrier charge for the call minutes, billed separately from the ElevenLabs agent cost.`,
        },
      },
      footer: sumFooter<UsageCostVoiceCallRow>((row) => row.telephonyCostUsd),
    },
    {
      accessorFn: (row) => row.elevenLabsAsrUsd,
      cell: (props) => <>{formatUsdOrDash(props.row.original.elevenLabsAsrUsd)}</>,
      header: `ASR`,
      id: `ASR`,
      meta: {
        group: `Cost Components`,
        defaultVisible: true,
        infoTooltip: { title: `ASR`, message: `Speech-to-text transcription of the caller's audio.` },
      },
      footer: sumFooter<UsageCostVoiceCallRow>((row) => row.elevenLabsAsrUsd),
    },
    {
      accessorFn: (row) => row.elevenLabsTtsUsd,
      cell: (props) => <>{formatUsdOrDash(props.row.original.elevenLabsTtsUsd)}</>,
      header: `TTS`,
      id: `TTS`,
      meta: {
        group: `Cost Components`,
        defaultVisible: true,
        infoTooltip: { title: `TTS`, message: `Text-to-speech synthesis of the agent's replies.` },
      },
      footer: sumFooter<UsageCostVoiceCallRow>((row) => row.elevenLabsTtsUsd),
    },
    {
      accessorFn: (row) => row.elevenLabsLlmUsd,
      cell: (props) => <>{formatUsdOrDash(props.row.original.elevenLabsLlmUsd)}</>,
      header: `LLM`,
      id: `LLM`,
      meta: {
        group: `Cost Components`,
        defaultVisible: true,
        infoTooltip: { title: `LLM`, message: `Model inference driving the agent's side of the conversation.` },
      },
      footer: sumFooter<UsageCostVoiceCallRow>((row) => row.elevenLabsLlmUsd),
    },
    {
      accessorFn: (row) => row.elevenLabsPlatformUsd,
      cell: (props) => <>{formatUsdOrDash(props.row.original.elevenLabsPlatformUsd)}</>,
      header: `Platform`,
      id: `Platform`,
      meta: { group: `Cost Components`, defaultVisible: true },
      footer: sumFooter<UsageCostVoiceCallRow>((row) => row.elevenLabsPlatformUsd),
    },
    {
      accessorFn: (row) => row.elevenLabsTotalUsd,
      cell: (props) => <>{formatUsdOrDash(props.row.original.elevenLabsTotalUsd)}</>,
      header: `ElevenLabs Total`,
      id: `ElevenLabs Total`,
      meta: {
        group: `Cost Components`,
        defaultVisible: false,
        infoTooltip: {
          title: `ElevenLabs Total`,
          message: `The total ElevenLabs bills for this call. ASR, TTS, LLM, and Platform are their own breakdown of it and may not add up to it exactly.`,
        },
      },
      footer: sumFooter<UsageCostVoiceCallRow>((row) => row.elevenLabsTotalUsd),
    },
    {
      accessorFn: (row) => row.llmInputTokens,
      cell: (props) => <MetricCell value={props.row.original.llmInputTokens} />,
      header: `Tokens In`,
      id: `Tokens In`,
      meta: { group: `Usage`, defaultVisible: false },
    },
    {
      accessorFn: (row) => row.llmOutputTokens,
      cell: (props) => <MetricCell value={props.row.original.llmOutputTokens} />,
      header: `Tokens Out`,
      id: `Tokens Out`,
      meta: { group: `Usage`, defaultVisible: false },
    },
    {
      accessorFn: (row) => row.totalCostUsd,
      cell: (props) => <>{formatUsdOrDash(props.row.original.totalCostUsd)}</>,
      header: `Total Cost`,
      id: `Total Cost`,
      meta: {
        group: `Cost Components`,
        defaultVisible: true,
        infoTooltip: { title: `Total Cost`, message: `Telephony plus the ElevenLabs total for this call.` },
      },
      footer: sumFooter<UsageCostVoiceCallRow>((row) => row.totalCostUsd),
    },
  ]
}

export function getUsageSmsThreadColumns(): ColumnDef<UsageCostSmsThreadRow, any>[] {
  return [
    {
      accessorFn: (row) => row.firstTurnDate,
      cell: (props) => <>{formatDateTime(props.row.original.firstTurnDate)}</>,
      header: `Started`,
      id: `Started`,
      meta: { group: `General`, defaultVisible: true, pinned: true },
      enableHiding: false,
    },
    {
      accessorFn: (row) => row.lastTurnDate,
      cell: (props) => <>{formatDateTime(props.row.original.lastTurnDate)}</>,
      header: `Last Message`,
      id: `Last Message`,
      meta: { group: `General`, defaultVisible: false },
    },
    {
      accessorFn: (row) => row.turns,
      cell: (props) => <MetricCell value={props.row.original.turns} />,
      header: `Turns`,
      id: `Turns`,
      meta: {
        group: `Usage`,
        defaultVisible: true,
        infoTooltip: {
          title: `Turns`,
          message: `Agent replies in this thread. Every turn re-sends the conversation history, so cost climbs faster than turn count.`,
        },
      },
    },
    {
      accessorFn: (row) => row.tokensInput,
      cell: (props) => <MetricCell value={props.row.original.tokensInput} />,
      header: `Tokens In`,
      id: `Tokens In`,
      meta: { group: `Usage`, defaultVisible: true },
    },
    {
      accessorFn: (row) => row.tokensOutput,
      cell: (props) => <MetricCell value={props.row.original.tokensOutput} />,
      header: `Tokens Out`,
      id: `Tokens Out`,
      meta: { group: `Usage`, defaultVisible: true },
    },
    {
      accessorFn: (row) => row.avgLatencyMs ?? 0,
      cell: (props) => {
        const value = props.row.original.avgLatencyMs
        return <>{value == null ? `—` : formatLatencyMs(value)}</>
      },
      header: `Avg Latency`,
      id: `Avg Latency`,
      meta: { group: `Usage`, defaultVisible: false },
    },
    {
      accessorFn: (row) => row.llmCostUsd,
      cell: (props) => <>{formatUsdOrDash(props.row.original.llmCostUsd)}</>,
      header: `LLM`,
      id: `LLM`,
      meta: {
        group: `Cost Components`,
        defaultVisible: true,
        infoTooltip: { title: `LLM`, message: `Model inference for generating this thread's replies.` },
      },
      footer: sumFooter<UsageCostSmsThreadRow>((row) => row.llmCostUsd),
    },
    {
      accessorFn: (row) => row.carrierCostUsd,
      cell: (props) => <>{formatUsdOrDash(props.row.original.carrierCostUsd)}</>,
      header: `Carrier`,
      id: `Carrier`,
      meta: {
        group: `Cost Components`,
        defaultVisible: true,
        infoTooltip: { title: `Carrier`, message: `Twilio per-segment charge for the messages sent in this thread.` },
      },
      footer: sumFooter<UsageCostSmsThreadRow>((row) => row.carrierCostUsd),
    },
    {
      accessorFn: (row) => row.totalCostUsd,
      cell: (props) => <>{formatUsdOrDash(props.row.original.totalCostUsd)}</>,
      header: `Total Cost`,
      id: `Total Cost`,
      meta: { group: `Cost Components`, defaultVisible: true },
      footer: sumFooter<UsageCostSmsThreadRow>((row) => row.totalCostUsd),
    },
  ]
}
